/**
 * Account Usage Detail Page
 * Detailed license utilization view for a single account
 */

import { useParams, Link } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { LoadingState, ErrorState } from '@/components/ui/loading-state'
import {
  AlertTriangle,
  ArrowLeft,
  ArrowDownCircle,
  ArrowUpCircle,
  BarChart3,
  Building2,
  Calendar,
  CheckCircle,
  Clock,
  DollarSign,
  Download,
  Target,
  TrendingDown,
  TrendingUp,
  AlertCircle,
  Gauge,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useAccountUsageById } from '@/hooks/useFinanceApi'
import { STATUS_CONFIG } from '@/features/finance/types'
import type { UsageStatus } from '@/features/finance/types'
import type { AccountUsageData } from '@/api/types'

// Format large numbers
function formatNumber(num: number): string {
  if (num >= 1_000_000_000) return (num / 1_000_000_000).toFixed(2) + 'B'
  if (num >= 1_000_000) return (num / 1_000_000).toFixed(2) + 'M'
  if (num >= 1_000) return (num / 1_000).toFixed(1) + 'K'
  return num.toLocaleString()
}

function formatCurrency(num: number): string {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(num)
}

// Status badge
function StatusBadge({ status, size = 'md' }: { status: UsageStatus; size?: 'sm' | 'md' | 'lg' }) {
  const config = STATUS_CONFIG[status]
  const sizeClasses = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-1.5 text-base',
  }

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full font-medium',
        config.bgColor,
        config.color,
        sizeClasses[size]
      )}
    >
      {status === 'critical' && <AlertTriangle className="h-4 w-4" />}
      {status === 'warning' && <AlertCircle className="h-4 w-4" />}
      {status === 'healthy' && <CheckCircle className="h-4 w-4" />}
      {status === 'underutilized' && <TrendingDown className="h-4 w-4" />}
      {config.label}
    </span>
  )
}

// Circular gauge component
function CircularGauge({
  percent,
  size = 200,
}: {
  percent: number
  size?: number
}) {
  const radius = (size - 20) / 2
  const circumference = 2 * Math.PI * radius
  const cappedPercent = Math.min(percent, 100)
  const strokeDashoffset = circumference - (cappedPercent / 100) * circumference

  const getColor = () => {
    if (percent > 100) return '#ef4444' // red
    if (percent > 90) return '#f59e0b' // amber
    if (percent < 50) return '#3b82f6' // blue
    return '#10b981' // emerald
  }

  return (
    <div className="relative inline-flex items-center justify-center">
      <svg width={size} height={size} className="transform -rotate-90">
        {/* Background circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="#e5e7eb"
          strokeWidth="12"
        />
        {/* Progress circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={getColor()}
          strokeWidth="12"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          className="transition-all duration-500"
        />
        {/* Overage indicator */}
        {percent > 100 && (
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius - 15}
            fill="none"
            stroke="#fecaca"
            strokeWidth="8"
            strokeDasharray={circumference * 0.8}
            strokeDashoffset={circumference * 0.8 - ((percent - 100) / 50) * circumference * 0.8}
            className="transition-all duration-500"
          />
        )}
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-0.5">
        <span className="text-3xl font-bold leading-tight" style={{ color: getColor() }}>
          {percent}%
        </span>
        {percent > 100 && (
          <span className="text-xs text-red-600 font-medium leading-tight">
            +{parseFloat((percent - 100).toFixed(1))}% over
          </span>
        )}
      </div>
    </div>
  )
}

// Monthly breakdown table
function MonthlyBreakdownTable({ account, monthlyAverage }: { account: AccountUsageData; monthlyAverage: number }) {
  const monthlyUsage = account.monthlyUsage || []

  if (monthlyUsage.length === 0) {
    return <div className="text-sm text-gray-500 text-center py-4">No monthly data available</div>
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wider">
            <th className="py-3 px-4 text-left font-medium">Month</th>
            <th className="py-3 px-4 text-right font-medium">Data In</th>
            <th className="py-3 px-4 text-right font-medium">Data Out</th>
            <th className="py-3 px-4 text-right font-medium">Total</th>
            <th className="py-3 px-4 text-right font-medium">vs License</th>
            <th className="py-3 px-4 text-left font-medium">Status</th>
          </tr>
        </thead>
        <tbody>
          {monthlyUsage.map((month, idx) => {
            const vsLicense = monthlyAverage > 0 ? (month.totalEvents / monthlyAverage) * 100 : 0
            const isOver = vsLicense > 100
            const monthDate = new Date(month.month + '-01')
            const isCurrentMonth = idx === monthlyUsage.length - 1

            return (
              <tr
                key={month.month}
                className={cn(
                  'border-b border-gray-100',
                  isCurrentMonth && 'bg-indigo-50'
                )}
              >
                <td className="py-3 px-4">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900">
                      {monthDate.toLocaleDateString('en-US', {
                        month: 'long',
                        year: 'numeric',
                      })}
                    </span>
                    {isCurrentMonth && (
                      <span className="text-xs px-1.5 py-0.5 bg-indigo-100 text-indigo-700 rounded">
                        Current
                      </span>
                    )}
                  </div>
                </td>
                <td className="py-3 px-4 text-right">
                  <span className="text-emerald-600 font-medium">
                    {formatNumber(month.dataIn)}
                  </span>
                </td>
                <td className="py-3 px-4 text-right">
                  <span className="text-blue-600 font-medium">
                    {formatNumber(month.dataOut)}
                  </span>
                </td>
                <td className="py-3 px-4 text-right">
                  <span className="font-semibold text-gray-900">
                    {formatNumber(month.totalEvents)}
                  </span>
                </td>
                <td className="py-3 px-4 text-right">
                  <span
                    className={cn(
                      'font-medium',
                      isOver ? 'text-amber-600' : 'text-gray-600'
                    )}
                  >
                    {vsLicense.toFixed(0)}%
                  </span>
                </td>
                <td className="py-3 px-4">
                  {isOver ? (
                    <span className="inline-flex items-center gap-1 text-xs text-amber-600">
                      <AlertCircle className="h-3 w-3" />
                      Above avg
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-xs text-emerald-600">
                      <CheckCircle className="h-3 w-3" />
                      Normal
                    </span>
                  )}
                </td>
              </tr>
            )
          })}
        </tbody>
        <tfoot>
          <tr className="bg-gray-50 font-semibold">
            <td className="py-3 px-4 text-gray-700">Total YTD</td>
            <td className="py-3 px-4 text-right text-emerald-600">
              {formatNumber(monthlyUsage.reduce((sum, m) => sum + m.dataIn, 0))}
            </td>
            <td className="py-3 px-4 text-right text-blue-600">
              {formatNumber(monthlyUsage.reduce((sum, m) => sum + m.dataOut, 0))}
            </td>
            <td className="py-3 px-4 text-right text-gray-900">
              {formatNumber(account.yearToDateUsage)}
            </td>
            <td className="py-3 px-4 text-right text-gray-600">-</td>
            <td className="py-3 px-4">-</td>
          </tr>
        </tfoot>
      </table>
    </div>
  )
}

// Bar chart for monthly visualization
function MonthlyBarChart({ account, monthlyAverage }: { account: AccountUsageData; monthlyAverage: number }) {
  const monthlyUsage = account.monthlyUsage || []

  if (monthlyUsage.length === 0) {
    return <div className="h-64 flex items-center justify-center text-sm text-gray-500">No monthly data available</div>
  }

  const maxValue = Math.max(
    ...monthlyUsage.map((m) => m.totalEvents),
    monthlyAverage * 1.2
  )

  return (
    <div className="h-64">
      <div className="flex items-end justify-between h-full gap-2 pb-8 relative">
        {/* License line */}
        {monthlyAverage > 0 && (
          <div
            className="absolute left-0 right-0 border-t-2 border-dashed border-gray-400 z-10"
            style={{
              bottom: `${(monthlyAverage / maxValue) * 100}%`,
            }}
          >
            <span className="absolute right-0 -top-5 text-xs text-gray-500 bg-white px-1">
              License avg: {formatNumber(monthlyAverage)}
            </span>
          </div>
        )}

        {monthlyUsage.map((month, idx) => {
          const height = (month.totalEvents / maxValue) * 100
          const isOver = month.totalEvents > monthlyAverage
          const isCurrentMonth = idx === monthlyUsage.length - 1

          return (
            <div key={month.month} className="flex-1 flex flex-col items-center">
              <div
                className={cn(
                  'w-full rounded-t transition-all relative group',
                  isOver ? 'bg-amber-400 hover:bg-amber-500' : 'bg-emerald-400 hover:bg-emerald-500',
                  isCurrentMonth && 'ring-2 ring-indigo-400'
                )}
                style={{ height: `${height}%` }}
              >
                {/* Tooltip */}
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-20">
                  {formatNumber(month.totalEvents)} events
                </div>
              </div>
              <span className="text-[10px] text-gray-500 mt-2 absolute bottom-0">
                {new Date(month.month + '-01').toLocaleDateString('en-US', {
                  month: 'short',
                })}
              </span>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// Helper to calculate average monthly usage
function getAverageMonthlyUsage(account: AccountUsageData): number {
  if (!account.monthlyUsage || account.monthlyUsage.length === 0) return 0
  const total = account.monthlyUsage.reduce((sum, m) => sum + m.totalEvents, 0)
  return Math.floor(total / account.monthlyUsage.length)
}

// Calculate months remaining in year
function getMonthsRemaining(): number {
  const today = new Date()
  return 12 - (today.getMonth() + 1)
}

function formatNum(n: number): string {
  if (n >= 1_000_000_000) return (n / 1_000_000_000).toFixed(2) + 'B'
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(2) + 'M'
  if (n >= 1_000) return (n / 1_000).toFixed(1) + 'K'
  return n.toLocaleString()
}

export function AccountUsagePage() {
  const { accountId } = useParams<{ accountId: string }>()
  const { data: account, loading, error, refetch } = useAccountUsageById(Number(accountId))

  if (loading) {
    return (
      <div className="p-6">
        <LoadingState message="Loading account data..." />
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-6">
        <ErrorState error={error} message="Failed to load account data" onRetry={refetch} />
      </div>
    )
  }

  if (!account) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <Building2 className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900">Account Not Found</h2>
          <p className="text-gray-500 mt-2">The requested account could not be found.</p>
          <Link to="/finance">
            <Button className="mt-4">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Finance Dashboard
            </Button>
          </Link>
        </div>
      </div>
    )
  }

  const overageRate = account.license?.overageRate || 0
  const projectedOverageCost =
    account.projectedOverage > 0
      ? account.projectedOverage * overageRate
      : 0

  const yearlyLimit = account.license?.yearlyLimit || 0
  const monthlyAverage = account.license?.monthlyAverage || 0
  const tierName = account.license?.tier || 'Unknown'
  const pricePerEvent = account.license?.pricePerEvent || 0
  const monthsRemaining = getMonthsRemaining()
  const averageMonthlyUsage = getAverageMonthlyUsage(account)

  const remainingBudget = yearlyLimit - account.yearToDateUsage
  const monthlyBudgetRemaining =
    monthsRemaining > 0 ? remainingBudget / monthsRemaining : 0

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <Link
            to="/finance"
            className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Finance Dashboard
          </Link>
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 bg-indigo-100 rounded-xl flex items-center justify-center">
              <Building2 className="h-6 w-6 text-indigo-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
                {account.accountName}
                <StatusBadge status={account.status as UsageStatus} size="md" />
              </h1>
              <div className="flex items-center gap-3 text-sm text-gray-500 mt-1">
                <span>Account #{account.accId}</span>
                <span>|</span>
                <span className="px-2 py-0.5 bg-gray-100 rounded font-medium">
                  {tierName} Plan
                </span>
                <span>|</span>
                <span>{formatNum(yearlyLimit)} events/year</span>
                <span>|</span>
                <span>{formatNum(monthlyAverage)}/month avg</span>
                <span>|</span>
                <span>{formatCurrency(pricePerEvent * yearlyLimit)}/year</span>
                <span>|</span>
                <span>₹{(overageRate * 1000).toFixed(0)}/1K overage</span>
              </div>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" className="gap-2">
            <Download className="h-4 w-4" />
            Export
          </Button>
        </div>
      </div>

      {/* Alert Banner */}
      {account.status === 'critical' && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-semibold text-red-800">Overage Alert</h3>
            <p className="text-sm text-red-700 mt-1">
              This account is projected to exceed its yearly license limit by{' '}
              <strong>{formatNumber(account.projectedOverage)}</strong> events.
              Estimated overage charges: <strong>{formatCurrency(projectedOverageCost)}</strong>
            </p>
          </div>
        </div>
      )}

      {account.status === 'underutilized' && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-3">
          <TrendingDown className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-semibold text-blue-800">Underutilization Notice</h3>
            <p className="text-sm text-blue-700 mt-1">
              This account is using only <strong>{account.utilizationPercent}%</strong> of its
              licensed capacity. Consider discussing a plan adjustment to better match their needs.
            </p>
          </div>
        </div>
      )}

      {/* Main Stats */}
      <div className="grid grid-cols-3 gap-6">
        {/* Utilization Gauge */}
        <Card className="col-span-1">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Gauge className="h-4 w-4 text-gray-500" />
              YTD Utilization
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col items-center pt-4">
            <CircularGauge
              percent={account.utilizationPercent}
              size={180}
            />
            <div className="mt-3 text-center space-y-0.5">
              <div className="text-xs text-gray-400">of prorated limit</div>
              <div className="text-sm text-gray-600">
                {formatNumber(account.yearToDateUsage)} / {formatNumber(yearlyLimit)}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Key Metrics */}
        <Card className="col-span-2">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Target className="h-4 w-4 text-gray-500" />
              License & Usage Metrics
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4">
              <div className="p-4 bg-gray-50 rounded-xl">
                <div className="flex items-center gap-2 text-gray-500 mb-1">
                  <Calendar className="h-4 w-4" />
                  <span className="text-xs">Yearly License</span>
                </div>
                <div className="text-2xl font-bold text-gray-900">
                  {formatNumber(yearlyLimit)}
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  {formatNumber(monthlyAverage)}/month avg
                </div>
              </div>

              <div className="p-4 bg-gray-50 rounded-xl">
                <div className="flex items-center gap-2 text-gray-500 mb-1">
                  <TrendingUp className="h-4 w-4" />
                  <span className="text-xs">Avg Monthly Usage</span>
                </div>
                <div className="text-2xl font-bold text-gray-900">
                  {formatNumber(averageMonthlyUsage)}
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  {monthlyAverage > 0 ? ((averageMonthlyUsage / monthlyAverage) * 100).toFixed(0) : 0}%
                  of license
                </div>
              </div>

              <div className="p-4 bg-gray-50 rounded-xl">
                <div className="flex items-center gap-2 text-gray-500 mb-1">
                  <Clock className="h-4 w-4" />
                  <span className="text-xs">Months Remaining</span>
                </div>
                <div className="text-2xl font-bold text-gray-900">
                  {monthsRemaining}
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  in contract year
                </div>
              </div>

              <div className="p-4 bg-emerald-50 rounded-xl">
                <div className="flex items-center gap-2 text-emerald-600 mb-1">
                  <ArrowDownCircle className="h-4 w-4" />
                  <span className="text-xs">Data In (YTD)</span>
                </div>
                <div className="text-2xl font-bold text-emerald-700">
                  {formatNumber(account.monthlyUsage.reduce((sum, m) => sum + m.dataIn, 0))}
                </div>
              </div>

              <div className="p-4 bg-blue-50 rounded-xl">
                <div className="flex items-center gap-2 text-blue-600 mb-1">
                  <ArrowUpCircle className="h-4 w-4" />
                  <span className="text-xs">Data Out (YTD)</span>
                </div>
                <div className="text-2xl font-bold text-blue-700">
                  {formatNumber(account.monthlyUsage.reduce((sum, m) => sum + m.dataOut, 0))}
                </div>
              </div>

              <div
                className={cn(
                  'p-4 rounded-xl',
                  account.projectedOverage > 0 ? 'bg-red-50' : 'bg-gray-50'
                )}
              >
                <div
                  className={cn(
                    'flex items-center gap-2 mb-1',
                    account.projectedOverage > 0 ? 'text-red-600' : 'text-gray-500'
                  )}
                >
                  <DollarSign className="h-4 w-4" />
                  <span className="text-xs">Projected Overage</span>
                </div>
                <div
                  className={cn(
                    'text-2xl font-bold',
                    account.projectedOverage > 0 ? 'text-red-700' : 'text-gray-900'
                  )}
                >
                  {account.projectedOverage > 0
                    ? formatNumber(account.projectedOverage)
                    : 'None'}
                </div>
                {account.projectedOverage > 0 && (
                  <div className="text-xs text-red-600 mt-1">
                    ~{formatCurrency(projectedOverageCost)} charges
                  </div>
                )}
              </div>
            </div>

            {/* Budget Guidance */}
            <div className="mt-4 p-4 bg-indigo-50 rounded-xl">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium text-indigo-900">Remaining Budget</h4>
                  <p className="text-sm text-indigo-700">
                    {remainingBudget > 0 ? (
                      <>
                        <strong>{formatNumber(remainingBudget)}</strong> events remaining for the year
                        ({formatNumber(monthlyBudgetRemaining)}/month for next {monthsRemaining} months)
                      </>
                    ) : (
                      <>
                        Budget exceeded by <strong>{formatNumber(Math.abs(remainingBudget))}</strong> events
                      </>
                    )}
                  </p>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-indigo-700">
                    {formatNumber(account.projectedYearlyUsage)}
                  </div>
                  <div className="text-xs text-indigo-600">Projected yearly</div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Monthly Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <BarChart3 className="h-4 w-4 text-gray-500" />
            Monthly Usage Trend
          </CardTitle>
        </CardHeader>
        <CardContent>
          <MonthlyBarChart account={account} monthlyAverage={monthlyAverage} />
          <div className="flex items-center justify-center gap-6 mt-4 text-xs text-gray-500">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-emerald-400 rounded" />
              <span>Within license average</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-amber-400 rounded" />
              <span>Above license average</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-0.5 border-t-2 border-dashed border-gray-400" style={{ width: 16 }} />
              <span>Monthly license average</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Monthly Breakdown Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Calendar className="h-4 w-4 text-gray-500" />
            Monthly Breakdown
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <MonthlyBreakdownTable account={account} monthlyAverage={monthlyAverage} />
        </CardContent>
      </Card>

    </div>
  )
}

export default AccountUsagePage
